import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";
import MatrixBackground from "../components/MatrixBackground";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid-bg noise isolate flex items-center justify-center p-4" data-testid="forgot-password-container">
      <MatrixBackground opacity={0.25} />
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1 text-slate-400 hover:text-cyan-300 text-sm mb-6" data-testid="back-to-login">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
        <div className="glass rounded-3xl p-7 ring-cyan">
          <Logo size={34} />
          <h1 className="heading-font text-3xl font-extrabold mt-6">Forgot password?</h1>
          <p className="text-slate-400 text-sm mt-1">Enter your email and we'll send you a reset link.</p>

          {sent ? (
            <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="forgot-password-success">
              <div className="flex items-center gap-2 text-emerald-300 font-bold"><MailCheck size={16} /> Check your inbox</div>
              <p className="text-sm text-slate-300 mt-1">{sent.message}</p>
              {sent.dev_reset_link && (
                <div className="mt-3 text-xs text-slate-400">
                  Preview mode:{" "}
                  <Link to={sent.dev_reset_link} className="text-cyan-300 font-semibold underline" data-testid="dev-reset-link">
                    open reset link
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4 mt-6" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="fp-email" className="text-slate-300 text-xs uppercase tracking-widest font-bold">Email</Label>
                <Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-11 bg-white/5 border-cyan-500/15 text-white placeholder:text-slate-500"
                  placeholder="you@email.com" data-testid="forgot-password-email" />
              </div>
              <Button type="submit" className="btn-neon w-full h-11 rounded-full" disabled={loading} data-testid="forgot-password-submit">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
